import { useState, useEffect } from 'react';
import { Loader2, Plus, CreditCard as Edit2, Archive, Trash2, RotateCcw, Calendar, Clock, Eye, MoreVertical } from 'lucide-react';
import AdminShell from '../components/AdminShell';
import Toast from '../components/Toast';
import WeeklyCardBack from '../components/WeeklyCardBack';
import PublishedEditionModal from '../components/PublishedEditionModal';
import { useApp } from '../lib/AppContext';
import {
  getActiveEdition,
  getLatestPublishedEdition,
  getNextScheduledEdition,
  listEditionsByStatus,
  archiveWeek,
  unarchiveWeek,
  deleteWeek,
  scheduleWeek,
  publishNow,
  WeeklyCardWeek,
  WeekWithCards,
  checkAndActivateScheduledEditions,
  getPublishedHistory,
  PublishedHistoryItem,
  getWeekById,
} from '../lib/weeklyCardsPlanner';
import { archiveEdition, deleteEdition } from '../lib/weeklyEditionActions';

type TabType = 'published' | 'scheduled' | 'drafts' | 'archived';

function formatWeekLabel(weekOf: string): string {
  const date = new Date(weekOf + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AdminWeeklyCardsOverview() {
  const { navigateTo, currentScreen } = useApp();
  const [activeTab, setActiveTab] = useState<TabType>('published');
  const [loading, setLoading] = useState(true);
  const [activeEdition, setActiveEdition] = useState<WeekWithCards | null>(null);
  const [fallbackPreviewEdition, setFallbackPreviewEdition] = useState<WeekWithCards | null>(null);
  const [editions, setEditions] = useState<WeeklyCardWeek[]>([]);
  const [publishedHistory, setPublishedHistory] = useState<PublishedHistoryItem[]>([]);

  const [showPublishNowModal, setShowPublishNowModal] = useState<string | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState<string | null>(null);
  const [scheduleDateTime, setScheduleDateTime] = useState('');
  const [viewedEdition, setViewedEdition] = useState<WeekWithCards | null>(null);
  const [showArchiveModal, setShowArchiveModal] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<{ weekId: string; status: string } | null>(null);
  const [openOverflowMenu, setOpenOverflowMenu] = useState<string | null>(null);

  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success',
  });

  useEffect(() => {
    initializePage();
  }, []);

  // Refresh data when navigating back to this page
  useEffect(() => {
    if (currentScreen === 'admin-weekly-cards') {
      console.log('[AdminWeeklyCardsOverview] Screen became active, refreshing data. Tab:', activeTab);
      if (activeTab === 'published') {
        loadActiveTab();
      } else {
        loadEditionsByTab();
      }
    }
  }, [currentScreen]);

  useEffect(() => {
    if (activeTab === 'published') {
      loadActiveTab();
    } else {
      loadEditionsByTab();
    }
  }, [activeTab]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (openOverflowMenu && !target.closest('[data-overflow-menu]')) {
        setOpenOverflowMenu(null);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openOverflowMenu) {
        setOpenOverflowMenu(null);
      }
    };
    if (openOverflowMenu) {
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
      }, 0);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openOverflowMenu]);

  async function initializePage() {
    await checkAndActivateScheduledEditions();
    if (activeTab === 'published') {
      await loadActiveTab();
    } else {
      await loadEditionsByTab();
    }
  }

  async function loadActiveTab() {
    console.log('[AdminWeeklyCardsOverview] Loading active tab...');
    setLoading(true);
    const [active, latestPublished, nextScheduled] = await Promise.all([
      getActiveEdition(),
      getLatestPublishedEdition(),
      getNextScheduledEdition(),
    ]);

    console.log('[AdminWeeklyCardsOverview] Active edition loaded:', active);
    setActiveEdition(active);

    if (!active) {
      setFallbackPreviewEdition(latestPublished || nextScheduled || null);
    } else {
      setFallbackPreviewEdition(null);
    }

    const history = await getPublishedHistory(active?.week.id);
    setPublishedHistory(history);
    setLoading(false);
  }

  async function loadEditionsByTab() {
    setLoading(true);
    const status = activeTab === 'drafts' ? 'draft' : activeTab;
    const list = await listEditionsByStatus(status);
    setEditions(list);
    setLoading(false);
  }

  async function handleArchiveConfirm() {
    if (!showArchiveModal) return;

    const result = await archiveEdition(showArchiveModal);
    if (result.success) {
      showToast('Archived', 'success');
      setShowArchiveModal(null);
      if (activeTab === 'published') {
        await loadActiveTab();
      } else {
        await loadEditionsByTab();
      }
    } else {
      showToast(result.error || 'Failed to archive', 'error');
    }
  }

  async function handleDeleteConfirm() {
    if (!showDeleteModal) return;

    const result = await deleteEdition(showDeleteModal.weekId);
    if (result.success) {
      showToast('Deleted', 'success');
      setShowDeleteModal(null);
      await loadEditionsByTab();
    } else {
      showToast(result.error || 'Failed to delete', 'error');
    }
  }

  async function handlePublishNow(weekId: string) {
    try {
      const success = await publishNow(weekId);
      if (success) {
        showToast('Published successfully', 'success');
        setShowPublishNowModal(null);
        setActiveTab('published');
        await loadActiveTab();
      } else {
        showToast('Failed to publish', 'error');
      }
    } catch (err) {
      console.error('[AdminWeeklyCardsOverview] Error publishing:', err);
      showToast('Failed to publish', 'error');
    }
  }

  async function handleSchedule(weekId: string) {
    if (!scheduleDateTime) {
      showToast('Please select a date and time', 'error');
      return;
    }

    try {
      await scheduleWeek(weekId, scheduleDateTime);
      const dateObj = new Date(scheduleDateTime);
      showToast(`Scheduled for ${dateObj.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`, 'success');
      setShowScheduleModal(null);
      setScheduleDateTime('');
      loadEditionsByTab();
    } catch (err) {
      console.error('[AdminWeeklyCardsOverview] Error scheduling:', err);
      showToast('Failed to schedule', 'error');
    }
  }

  function showToast(message: string, type: 'success' | 'error') {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  }

  async function handleViewPublishedEdition(weekId: string) {
    const edition = await getWeekById(weekId);
    if (edition) {
      setViewedEdition(edition);
    }
  }

  const hasScheduledEditions = editions.length > 0 && activeTab === 'scheduled';

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 lg:p-8 w-full max-w-[1120px]">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-[24px] sm:text-[28px] font-semibold text-[#F5F5F5] mb-2">Weekly Cards</h1>
            <p className="text-[13px] sm:text-[14px] text-[#808080]">
              Manage the three cards that appear on the Home screen each week
            </p>
          </div>
          <button
            onClick={() => navigateTo('admin-weekly-cards-create')}
            className="w-full sm:w-auto px-5 py-2.5 bg-[#40E0D0] hover:bg-[#47EAD8] text-[#0A0A0A] rounded-lg text-[14px] font-semibold transition-all flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Weekly Edition
          </button>
        </div>

        <div className="flex gap-1 mb-6 border-b border-[#282828] overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          {(['published', 'scheduled', 'drafts', 'archived'] as TabType[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-[14px] font-medium capitalize transition-colors relative ${
                activeTab === tab
                  ? 'text-[#40E0D0]'
                  : 'text-[#808080] hover:text-[#F5F5F5]'
              }`}
            >
              {tab}
              {activeTab === tab && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#40E0D0]" />
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#40E0D0] animate-spin" />
          </div>
        ) : activeTab === 'published' ? (
          <>
            {activeEdition || fallbackPreviewEdition ? (
              <>
                <div className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-4 sm:p-6 lg:p-8 mb-6">
                  <div className="mb-6 flex flex-col items-center">
                    <div className="w-full max-w-[948px] flex flex-col sm:flex-row items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-[16px] sm:text-[18px] font-semibold text-[#F5F5F5]">Active</h3>
                          {activeEdition ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium uppercase tracking-wide bg-[#40E0D0]/10 text-[#40E0D0]">
                              Live
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium uppercase tracking-wide bg-[#808080]/10 text-[#808080]">
                              Not Set
                            </span>
                          )}
                        </div>
                        {activeEdition ? (
                          <p className="text-[12px] text-[#606060]">
                            Last updated {(() => {
                              // Use week's updated_at (kept in sync by trigger), or fallback to most recent card
                              let timestamp = activeEdition.week.updated_at;

                              if (activeEdition.cards.length > 0) {
                                const mostRecentCard = activeEdition.cards.reduce((latest, card) => {
                                  const cardTime = new Date(card.updated_at).getTime();
                                  const latestTime = new Date(latest.updated_at).getTime();
                                  return cardTime > latestTime ? card : latest;
                                }, activeEdition.cards[0]);

                                // Use whichever is more recent
                                const weekTime = new Date(activeEdition.week.updated_at).getTime();
                                const cardTime = new Date(mostRecentCard.updated_at).getTime();
                                timestamp = cardTime > weekTime ? mostRecentCard.updated_at : activeEdition.week.updated_at;
                              }

                              return new Date(timestamp).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              });
                            })()}
                          </p>
                        ) : (
                          <p className="text-[13px] text-[#808080] mb-4">
                            No active edition is set. Showing the most recent {fallbackPreviewEdition?.week.status === 'published' ? 'published' : 'scheduled'} edition below.
                          </p>
                        )}
                      </div>
                      {activeEdition && (
                        <button
                          onClick={() => navigateTo('admin-weekly-cards-edit-live')}
                          className="w-full sm:w-auto px-5 py-2.5 bg-[#40E0D0] hover:bg-[#47EAD8] text-[#0A0A0A] rounded-lg text-[14px] font-semibold transition-all flex items-center justify-center gap-2"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit Active
                        </button>
                      )}
                    </div>
                    {!activeEdition && fallbackPreviewEdition && (
                      <div className="w-[948px] mb-4">
                        <p className="text-[11px] text-[#606060] uppercase tracking-wide">
                          Previewing: {fallbackPreviewEdition.week.status === 'published'
                            ? `Published ${new Date(fallbackPreviewEdition.week.published_at!).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                            : `Scheduled for ${new Date(fallbackPreviewEdition.week.publish_at!).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                          } (read-only)
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col lg:flex-row items-center justify-center gap-6">
                    {(() => {
                      const previewEdition = activeEdition || fallbackPreviewEdition;
                      if (!previewEdition) return null;

                      const artistWisdom = previewEdition.cards.find(c => c.card_type === 'artist_wisdom');
                      const civicMoment = previewEdition.cards.find(c => c.card_type === 'civic_moment');
                      const sparkThought = previewEdition.cards.find(c => c.card_type === 'spark_a_thought');

                      return (
                        <>
                          <div
                            className="origin-top-left"
                            style={{
                              width: '300px',
                              height: '400px',
                              transform: 'scale(0.833)',
                            }}
                          >
                            <div className="w-[360px] h-[480px] bg-bg-card rounded-[20px] border border-stroke flex flex-col"
                              style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}
                            >
                              {artistWisdom?.content ? (
                                <WeeklyCardBack
                                  cardType="artist_wisdom"
                                  imageUrl={artistWisdom.image_url}
                                  content={artistWisdom.content}
                                  attribution={artistWisdom.attribution}
                                />
                              ) : (
                                <div className="flex items-center justify-center h-full">
                                  <p className="text-[13px] text-[#606060] text-center">Not set</p>
                                </div>
                              )}
                            </div>
                          </div>

                          <div
                            className="origin-top-left"
                            style={{
                              width: '300px',
                              height: '400px',
                              transform: 'scale(0.833)',
                            }}
                          >
                            <div className="w-[360px] h-[480px] bg-bg-card rounded-[20px] border border-stroke flex flex-col"
                              style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}
                            >
                              {civicMoment?.content ? (
                                <WeeklyCardBack
                                  cardType="civic_moment"
                                  imageUrl={civicMoment.story_image_url || civicMoment.country?.image_url}
                                  content={civicMoment.content}
                                  title={civicMoment.title}
                                  countryName={civicMoment.country?.name}
                                  countryCode={civicMoment.country?.iso3}
                                  headline={civicMoment.headline}
                                  eventDate={civicMoment.event_date}
                                  sourceName={civicMoment.source_name}
                                  sourceUrl={civicMoment.source_url}
                                />
                              ) : (
                                <div className="flex items-center justify-center h-full">
                                  <p className="text-[13px] text-[#606060] text-center">Not set</p>
                                </div>
                              )}
                            </div>
                          </div>

                          <div
                            className="origin-top-left"
                            style={{
                              width: '300px',
                              height: '400px',
                              transform: 'scale(0.833)',
                            }}
                          >
                            <div className="w-[360px] h-[480px] bg-bg-card rounded-[20px] border border-stroke flex flex-col"
                              style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}
                            >
                              {sparkThought?.content ? (
                                <WeeklyCardBack
                                  cardType="civic_spark"
                                  imageUrl={null}
                                  content={sparkThought.content}
                                  subtext={sparkThought.subtext}
                                />
                              ) : (
                                <div className="flex items-center justify-center h-full">
                                  <p className="text-[13px] text-[#606060] text-center">Not set</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {!activeEdition && (
                    <div className="flex items-center gap-3 mt-6 pt-6 border-t border-[#282828]">
                      <button
                        onClick={() => navigateTo('admin-weekly-cards-create')}
                        className="px-5 py-2.5 bg-[#40E0D0] hover:bg-[#47EAD8] text-[#0A0A0A] rounded-lg text-[14px] font-semibold transition-all inline-flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Create Weekly Edition
                      </button>
                      <button
                        onClick={() => setActiveTab('drafts')}
                        className="px-5 py-2.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg text-[14px] font-medium transition-colors"
                      >
                        Go to Drafts
                      </button>
                    </div>
                  )}
                </div>

                {publishedHistory.length > 0 && (
                  <div className="bg-[#141414] border border-[#282828] rounded-xl overflow-hidden">
                    <div className="px-6 py-4 bg-[#0A0A0A] border-b border-[#282828]">
                      <h3 className="text-[16px] font-semibold text-[#F5F5F5]">Published History</h3>
                    </div>
                    <div className="divide-y divide-[#282828] overflow-visible">
                      {publishedHistory.map((item) => (
                        <div
                          key={item.week.id}
                          className="px-6 py-4 flex items-center justify-between overflow-visible"
                        >
                          <div className="text-[14px] text-[#B0B0B0]">
                            {item.week.published_at
                              ? new Date(item.week.published_at).toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                })
                              : '—'}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewPublishedEdition(item.week.id)}
                              className="px-3 py-1.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded text-[12px] font-medium transition-colors flex items-center gap-1.5"
                              title="View"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              View
                            </button>
                            <div className="relative" data-overflow-menu>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenOverflowMenu(openOverflowMenu === item.week.id ? null : item.week.id);
                                }}
                                className="p-2 text-[#808080] hover:text-[#F5F5F5] hover:bg-[#282828] transition-colors rounded min-w-[32px] min-h-[32px] flex items-center justify-center"
                                title="More options"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </button>
                              {openOverflowMenu === item.week.id && (
                                <div
                                  className="absolute right-0 top-full mt-1 bg-[#1A1A1A] border border-[#282828] rounded-lg shadow-2xl z-[100] min-w-[140px] py-1"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.3)'
                                  }}
                                >
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowArchiveModal(item.week.id);
                                      setOpenOverflowMenu(null);
                                    }}
                                    className="w-full px-4 py-2.5 text-left text-[13px] text-[#F5F5F5] hover:bg-[#282828] transition-colors flex items-center gap-2 first:rounded-t-lg last:rounded-b-lg"
                                  >
                                    <Archive className="w-3.5 h-3.5" />
                                    Archive
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-12 text-center">
                <div className="max-w-md mx-auto">
                  <h3 className="text-[18px] font-semibold text-[#F5F5F5] mb-2">No active weekly cards</h3>
                  <p className="text-[14px] text-[#808080] mb-6">
                    Create or publish a weekly edition to show it on Home.
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={() => navigateTo('admin-weekly-cards-create')}
                      className="px-5 py-2.5 bg-[#40E0D0] hover:bg-[#47EAD8] text-[#0A0A0A] rounded-lg text-[14px] font-semibold transition-all inline-flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Create Weekly Edition
                    </button>
                    <button
                      onClick={() => setActiveTab('drafts')}
                      className="px-5 py-2.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg text-[14px] font-medium transition-colors"
                    >
                      Go to Drafts
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : editions.length === 0 ? (
          <div className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-12 text-center">
            <p className="text-[14px] text-[#808080]">No {activeTab} editions found</p>
          </div>
        ) : (
          <div className="bg-[#1A1A1A] border border-[#282828] rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#0A0A0A] border-b border-[#282828]">
                <tr>
                  <th className="px-6 py-3 text-left text-[11px] font-medium text-[#808080] uppercase tracking-wider">
                    Week Of
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-medium text-[#808080] uppercase tracking-wider">
                    Status
                  </th>
                  {activeTab === 'scheduled' && (
                    <th className="px-6 py-3 text-left text-[11px] font-medium text-[#808080] uppercase tracking-wider">
                      Scheduled For
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-[11px] font-medium text-[#808080] uppercase tracking-wider">
                    Updated
                  </th>
                  <th className="px-6 py-3 text-right text-[11px] font-medium text-[#808080] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#282828]">
                {editions.map((edition) => (
                  <tr
                    key={edition.id}
                    className={`hover:bg-[#1e1e1e] transition-colors ${activeTab !== 'archived' ? 'cursor-pointer' : ''}`}
                    onClick={activeTab !== 'archived' ? () => navigateTo('admin-weekly-cards-edit', undefined, { weekId: edition.id }) : undefined}
                  >
                    <td className="px-6 py-4 text-[14px] text-[#F5F5F5]">
                      {formatWeekLabel(edition.week_of)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium uppercase tracking-wide ${
                        edition.status === 'active'
                          ? 'bg-[#40E0D0]/10 text-[#40E0D0]'
                          : edition.status === 'published'
                          ? 'bg-[#40E0D0]/10 text-[#40E0D0]'
                          : edition.status === 'scheduled'
                          ? 'bg-blue-500/10 text-blue-400'
                          : edition.status === 'archived'
                          ? 'bg-[#808080]/10 text-[#606060]'
                          : 'bg-[#808080]/10 text-[#808080]'
                      }`}>
                        {edition.status}
                      </span>
                    </td>
                    {activeTab === 'scheduled' && (
                      <td className="px-6 py-4 text-[13px] text-[#808080]">
                        {edition.publish_at
                          ? new Date(edition.publish_at).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })
                          : '—'}
                      </td>
                    )}
                    <td className="px-6 py-4 text-[13px] text-[#808080]">
                      {new Date(edition.updated_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </td>
                    <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        {activeTab === 'archived' ? (
                          <>
                            <button
                              onClick={() => handleViewPublishedEdition(edition.id)}
                              className="px-3 py-1.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded text-[12px] font-medium transition-colors flex items-center gap-1.5"
                              title="View"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              View
                            </button>
                            <button
                              onClick={() => setShowDeleteModal({ weekId: edition.id, status: 'archived' })}
                              className="p-2 text-[#808080] hover:text-red-400 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            {activeTab === 'scheduled' && (
                              <button
                                onClick={() => setShowPublishNowModal(edition.id)}
                                className="px-3 py-1.5 bg-[#40E0D0] hover:bg-[#47EAD8] text-[#0A0A0A] rounded text-[12px] font-semibold transition-all flex items-center gap-1"
                                title="Publish now"
                              >
                                <Clock className="w-3 h-3" />
                                Publish Now
                              </button>
                            )}
                            {activeTab === 'drafts' && (
                              <button
                                onClick={() => {
                                  setShowScheduleModal(edition.id);
                                  const nextMonday = new Date();
                                  nextMonday.setDate(nextMonday.getDate() + ((1 + 7 - nextMonday.getDay()) % 7 || 7));
                                  nextMonday.setHours(9, 0, 0, 0);
                                  setScheduleDateTime(nextMonday.toISOString().slice(0, 16));
                                }}
                                className="px-3 py-1.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded text-[12px] font-medium transition-colors flex items-center gap-1"
                                title="Schedule"
                              >
                                <Calendar className="w-3 h-3" />
                                Schedule
                              </button>
                            )}
                            <button
                              onClick={() => navigateTo('admin-weekly-cards-edit', undefined, { weekId: edition.id })}
                              className="p-2 text-[#808080] hover:text-[#40E0D0] transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            {(edition.status === 'draft' || edition.status === 'scheduled') && (
                              <button
                                onClick={() => setShowDeleteModal({ weekId: edition.id, status: edition.status })}
                                className="p-2 text-[#808080] hover:text-red-400 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showPublishNowModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1A1A] border border-[#282828] rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h2 className="text-[20px] font-semibold text-[#F5F5F5] mb-3">Publish now?</h2>
            <p className="text-[14px] text-[#A0A0A0] mb-6 leading-relaxed">
              This will replace the current Active weekly cards immediately. The current Active edition will move to Published History.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowPublishNowModal(null)}
                className="px-5 py-2.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg text-[14px] font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handlePublishNow(showPublishNowModal)}
                className="px-5 py-2.5 bg-[#40E0D0] hover:bg-[#47EAD8] text-[#0A0A0A] rounded-lg text-[14px] font-semibold transition-all"
              >
                Publish now
              </button>
            </div>
          </div>
        </div>
      )}

      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1A1A] border border-[#282828] rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h2 className="text-[20px] font-semibold text-[#F5F5F5] mb-3">Schedule edition</h2>
            <p className="text-[14px] text-[#A0A0A0] mb-4">
              Choose when this edition should go live:
            </p>
            <input
              type="datetime-local"
              value={scheduleDateTime}
              onChange={(e) => setScheduleDateTime(e.target.value)}
              className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] focus:outline-none focus:border-[#40E0D0] mb-6"
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowScheduleModal(null);
                  setScheduleDateTime('');
                }}
                className="px-5 py-2.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg text-[14px] font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSchedule(showScheduleModal)}
                className="px-5 py-2.5 bg-[#40E0D0] hover:bg-[#47EAD8] text-[#0A0A0A] rounded-lg text-[14px] font-semibold transition-all"
              >
                Schedule
              </button>
            </div>
          </div>
        </div>
      )}

      {showArchiveModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1A1A] border border-[#282828] rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h2 className="text-[20px] font-semibold text-[#F5F5F5] mb-3">Archive this edition?</h2>
            <p className="text-[14px] text-[#A0A0A0] mb-6 leading-relaxed">
              This will move it out of Published History. You can still view it later in Archived.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowArchiveModal(null)}
                className="px-5 py-2.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg text-[14px] font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleArchiveConfirm}
                className="px-5 py-2.5 bg-[#40E0D0] hover:bg-[#47EAD8] text-[#0A0A0A] rounded-lg text-[14px] font-semibold transition-all"
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1A1A] border border-[#282828] rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h2 className="text-[20px] font-semibold text-[#F5F5F5] mb-3">
              Delete this {showDeleteModal.status} edition?
            </h2>
            <p className="text-[14px] text-[#A0A0A0] mb-6 leading-relaxed">
              {showDeleteModal.status === 'draft'
                ? 'This will permanently delete this draft edition.'
                : showDeleteModal.status === 'scheduled'
                ? 'This will permanently delete this scheduled edition and its publish time.'
                : 'This will permanently delete this archived edition and all its data.'}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteModal(null)}
                className="px-5 py-2.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg text-[14px] font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-[14px] font-semibold transition-all"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {viewedEdition && (
        <PublishedEditionModal
          edition={viewedEdition}
          onClose={() => setViewedEdition(null)}
        />
      )}

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'success' })}
        />
      )}
    </AdminShell>
  );
}
